import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Camera, ClipboardPaste } from 'lucide-react'
import { Banner, Button, Checkbox, Field, Progress, Row, Sheet, useToast } from '../ui'
import { useAddItems, useRemoveItem } from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { aisleOf, needsLookup, type Dict } from '../lib/aisles'
import { requestAisleLookup } from '../lib/groceryAisles'
import { formatLine, parseQty } from '../lib/listParse'
import { cleanOcrText, defaultTicks, parseRecipe, type RecipeRow } from '../lib/recipeLines'
import { topPositions } from '../lib/newPosition'
import { useBlurField } from '../lib/useBlurField'
import type { List, ListItem } from '../lib/types'

/**
 * Package B (docs/recipe-import-plan): paste a recipe, review the rows it
 * becomes, add them in one write.
 *
 * The text arrives however the phone can give it: on an iPhone the
 * keyboard's Scan Text reads a cookbook page straight into the field, and
 * Live Text copies a screenshot. `parseRecipe` turns it into rows as it is
 * typed — no button to press first — and each row shows the aisle it will
 * land in, so a miss is visible before anything is written. Ticked by
 * default except a staple and anything already open on the list
 * (`defaultTicks`); a tap on the line edits it, a swipe drops it.
 *
 * One write for every ticked row (`useAddItems`), then a toast whose Undo
 * removes exactly those ids. No `bump_usual`: a recipe's saffron is not a
 * usual. The Worker's `/aisle` is asked per genuine miss, as a typed add
 * would, and never before the dictionary has loaded.
 */
export function RecipeImportSheet({ list, items, known, dict, initialText = '', onClose }: {
  list: List
  items: ListItem[]
  known: Dict
  dict: Dict | null
  /** Text that arrived with the sheet — the Shortcut route's deep link. */
  initialText?: string
  onClose: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const addM = useAddItems()
  const removeM = useRemoveItem()
  const error = useWriteError(addM)
  const [text, setText] = useState(initialText)
  // B2: a photographed page read on the phone. `scan` is the progress of the
  // read in flight, null otherwise; `scanError` a sentence for the banner.
  const [scan, setScan] = useState<number | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  // Rows the paste produced, in a state of their own so an edit or a drop
  // survives the next keystroke only when the paste itself is unchanged:
  // a new paste is a new list of rows.
  const [edited, setEdited] = useState<{ from: string; rows: RecipeRow[]; ticks: boolean[] } | null>(null)
  const [editing, setEditing] = useState<number | null>(null)

  const open = useMemo(() => items.filter(i => !i.done_at).map(i => i.body), [items])
  const parsed = useMemo(() => parseRecipe(text), [text])
  const rows = edited?.from === text ? edited.rows : parsed
  const ticks = edited?.from === text ? edited.ticks : defaultTicks(parsed, open)
  const ticked = rows.filter((_, i) => ticks[i])

  function update(nextRows: RecipeRow[], nextTicks: boolean[]) {
    setEdited({ from: text, rows: nextRows, ticks: nextTicks })
  }
  const setTick = (i: number, on: boolean) => update(rows, ticks.map((t, k) => (k === i ? on : t)))
  const drop = (i: number) => update(rows.filter((_, k) => k !== i), ticks.filter((_, k) => k !== i))
  function save(i: number, line: string) {
    setEditing(null)
    const { body, qty } = parseQty(line)
    if (!body) { drop(i); return }
    update(rows.map((r, k) => (k === i ? { ...r, body, qty } : r)), ticks)
  }

  async function add() {
    if (!ticked.length || addM.isPending) return
    const slots = topPositions(items, ticked.length)
    let ids: string[] = []
    try {
      const res = await addM.mutateAsync({
        listId: list.id,
        rows: ticked.map((r, n) => ({ body: r.body, qty: r.qty, position: slots[n]! })),
      })
      ids = ((res as { data?: { id: string }[] | null }).data ?? []).map(r => r.id)
    } catch {
      return // the banner reads addM's own state
    }
    for (const r of ticked) {
      if (dict && aisleOf(r.body, known, dict) === 'Other' && needsLookup(r.body, known, dict)) {
        void requestAisleLookup(r.body, qc)
      }
    }
    onClose()
    toast({
      text: `Added ${ticked.length} from the recipe`,
      action: { label: 'Undo', onClick: () => { for (const id of ids) removeM.mutate(id) } },
    })
  }

  /** B2: the camera (or the photo library — the file input offers both)
   *  through the on-device engine, its text appended to whatever is in the
   *  field. The engine is a lazy chunk plus ~10 MB of files on the first
   *  scan per device; the progress bar is the read itself. */
  async function scanPage(file: File | null) {
    if (!file) return
    setScanError(null)
    setScan(0)
    try {
      const { readPage } = await import('../lib/ocr')
      const raw = await readPage(file, p => setScan(p))
      const cleaned = cleanOcrText(raw)
      if (!cleaned.trim()) { setScanError('Nothing readable on that photo. Closer, flatter, more light.'); return }
      setText(t => (t.trim() ? `${t.trim()}\n${cleaned}` : cleaned))
    } catch (err) {
      setScanError(`Could not read the page: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setScan(null)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  /** The clipboard, on a tap (a tap is what the browser's paste permission
   *  needs): the Shortcut route's fallback, and a one-tap paste on a phone
   *  where the field's own paste is a long-press away. */
  async function pasteClipboard() {
    try {
      const t = await navigator.clipboard.readText()
      if (t.trim()) setText(t)
      else setScanError('The clipboard is empty.')
    } catch {
      setScanError('Could not read the clipboard — long-press the field and paste instead.')
    }
  }

  const metaFor = (r: RecipeRow, i: number): string => {
    const aisle = aisleOf(r.body, known, dict ?? [])
    if (r.staple) return `staple · ${aisle}`
    if (!ticks[i] && !r.staple) return `on the list · ${aisle}`
    return aisle
  }

  return (
    // Not a snapping sheet, and the field does not take focus on open: the
    // first build did both, and on Doug's iPhone (17 Oct) the keyboard rose
    // while the sheet was still animating in, the pan ran against a half-open
    // box, and what he saw was a blank sheet with "Nothing to add yet"
    // floating mid-screen. Quick add — a field in a sheet that works — does
    // neither. A tap on the field runs the Sheet's own keyboard handling
    // (`focusSnapsToTop`), the same path every other form in the app takes.
    <Sheet
      open onOpenChange={o => { if (!o) onClose() }} title="Paste a recipe" className="recipe"
      actions={
        <Button variant="primary" block busy={addM.isPending} disabled={!ticked.length} onClick={() => void add()}>
          {ticked.length ? `Add ${ticked.length} to ${list.name}` : 'Nothing to add yet'}
        </Button>
      }
    >
      {error && <Banner tone="bad">{error}</Banner>}
      {scanError && <Banner tone="bad">{scanError}</Banner>}
      <div className="recipe__tools">
        <Button variant="secondary" size="sm" disabled={scan !== null} onClick={() => fileInput.current?.click()}>
          <Camera size={18} aria-hidden="true" /> Scan a page
        </Button>
        <Button variant="secondary" size="sm" disabled={scan !== null} onClick={() => void pasteClipboard()}>
          <ClipboardPaste size={18} aria-hidden="true" /> Paste
        </Button>
        <input
          ref={fileInput} type="file" accept="image/*" capture="environment" hidden
          onChange={e => void scanPage(e.currentTarget.files?.[0] ?? null)}
        />
      </div>
      {scan !== null && (
        <div className="recipe__scan">
          <Progress value={scan} max={1} label="Reading the page" />
          <p className="recipe__hint">{scan === 0 ? 'Loading the reader…' : `Reading the page… ${Math.round(scan * 100)}%`}</p>
        </div>
      )}
      <Field label="Recipe" className="recipe__field">
        <Field.Textarea
          rows={5} value={text} placeholder="Paste the ingredients, or scan the page with the keyboard's camera"
          onChange={e => setText(e.target.value)}
        />
      </Field>
      <p className="recipe__hint">
        On an iPhone, tap the field, then the keyboard's Scan Text button and point it at the page.
      </p>
      {rows.length > 0 && (
        <div className="recipe__rows">
          <p className="lists-grouphead"><span>{rows.length} ingredient{rows.length === 1 ? '' : 's'}</span></p>
          {rows.map((r, i) => (
            <RecipeRowView
              key={`${i}:${r.body}`} row={r} on={!!ticks[i]} meta={metaFor(r, i)} editing={editing === i}
              onTick={on => setTick(i, on)} onEdit={() => setEditing(i)} onCancel={() => setEditing(null)}
              onSave={line => save(i, line)} onDrop={() => drop(i)}
            />
          ))}
        </div>
      )}
    </Sheet>
  )
}

function RecipeRowView({ row, on, meta, editing, onTick, onEdit, onCancel, onSave, onDrop }: {
  row: RecipeRow; on: boolean; meta: string; editing: boolean
  onTick: (on: boolean) => void; onEdit: () => void; onCancel: () => void
  onSave: (line: string) => void; onDrop: () => void
}) {
  const [draft, setDraft] = useState(() => formatLine(row.body, row.qty))
  const keys = useBlurField(() => { setDraft(formatLine(row.body, row.qty)); onCancel() })
  const lead = <Checkbox size="md" checked={on} onChange={() => onTick(!on)} aria-label={`Add "${row.body}"`} />
  if (editing) {
    return (
      <Row
        className="u-row--list"
        leading={lead}
        title={
          <Field.Input
            autoFocus value={draft} className="grocery__editfield"
            onChange={e => setDraft(e.target.value)}
            onBlur={keys.onBlurOf(() => onSave(draft))}
            onKeyDown={keys.onKeyDown}
          />
        }
        meta={null}
      />
    )
  }
  return (
    <Row
      className={`u-row--list${on ? '' : ' recipe__row--off'}`}
      leading={lead}
      title={<button type="button" className="lists-itembody" onClick={onEdit}>{formatLine(row.body, row.qty)}</button>}
      meta={meta}
      swipe={{ left: { label: 'Remove', onAction: onDrop, leaves: true } }}
    />
  )
}
